import { createQueryKeys, type Query } from '@shared/api';

/**
 * Ключи запросов заказа техники: витрины разделов, заявка с её поддеревьями и история назначения.
 *
 * Заведены реестром, а не строками в окнах, по одной практической причине: один и тот же ответ
 * спрашивают несколько экранов, а гасят его мутации из других файлов, и разойдись ключ хоть одним
 * словом — окно показывало бы прежнее после собственной же правки. Ошибка при этом молчаливая:
 * запрос не падает, он просто отвечает старым. Историю назначения так читают четыре экрана, а
 * гасят шесть мутаций; таблицу смен — четыре места одним ключом, ради одного похода на сервер.
 *
 * `root` гасят почти все мутации модуля: назначение, правка срока, смены и планирование дня меняют
 * версию заявки, а её показывают списки, которым про поддеревья ничего не известно. Но не все —
 * ремонт истории и смена машиниста гасят вдобавок адресно (`repairState`, `history`), а поле
 * перегонов — свой `relocations`. Под корнем эти поддеревья и так накрываются; адресное гашение
 * стоит рядом не ради экономии, а чтобы правка, которая однажды разведёт корни, не потеряла их
 * молча. Снимать его как «заведомо лишнее» нельзя по той же причине.
 *
 * Формы ключей повторяют то, что экраны собирали литералами, сегмент в сегмент, — и теперь экраны
 * зовут эти семейства. Сегмент — это ячейка кэша, и «прибрать» её переносом значило бы отправить
 * экран читать пустоту. Где форма выглядит случайной, это сказано прямо у самого семейства, а не
 * исправлено молча: правка кэша видна человеку и делается отдельной работой, а не попутной.
 *
 * Отсюда цена этих форм, которую стоит знать заранее. Ключ карточки — `['vehicle-requests', id]`,
 * без имени семейства, то есть префикс над всеми поддеревьями заявки: событиями, водителем,
 * листами, перегонами, днями и тремя ключами истории назначения. Погасив карточку, погасят и их
 * все разом. Кроме смен: у `shifts` имя семейства стоит перед идентификатором, и под карточку
 * этот ключ не попадает вовсе.
 */
export const vehicleRequestKeys = createQueryKeys('vehicle-requests', {
  /**
   * Лента раздела: заказы ТС и недельные заявки одним списком (ADR 0085). Отдельный маршрут, а не
   * флаг у списка: тем списком пользуются архив и подбор заявок в рейс, и недельный документ,
   * который в рейс не ставится, был бы там строкой, которую нельзя выбрать.
   *
   * Отбор объектом целиком, а не полями врозь: новый фильтр вкладки попадает в ключ сам, а
   * перечисленный руками список однажды забыли бы дополнить — и лента показала бы прежние строки
   * под новым фильтром.
   */
  feed: (params: Query) => ['feed', params],
  /**
   * Счётчики над лентой: сколько заявок не обработано, сколько ждёт визы, сколько в работе.
   *
   * Свой ключ и свой отбор, а не поле в ответе ленты: сводку считают по всему отбору, а список
   * отдаётся страницами — одним телом они разошлись бы на первом же листании. Отбор при этом не
   * тот же, что у `feed`: статус и номер из него выброшены, они свели бы сводку к ней самой, — и
   * общий ключ заставил бы одну ячейку хранить ответы на два разных вопроса.
   */
  summary: (params: Query) => ['summary', params],
  /**
   * Журнал закрытых заявок — вкладка «История» (ADR 0029): «Выполнена» и «Отменена» с фактом
   * выполнения. Свой маршрут: свой фильтр по арендодателю и свой порядок, по сроку работ.
   *
   * Имя не `history`: под ним здесь давно живёт история назначения, и это не мелочь разметки — за
   * двумя именами стоят два разных вопроса. Сегмент при этом остаётся `'history'`, его собирает
   * экран, и переименование сегмента сменило бы ячейку кэша.
   */
  closedList: (params: Query) => ['history', params],
  /**
   * Итог того же журнала по тем же фильтрам — отдельной ручкой по той же причине, что и сводка
   * ленты: итог считают по всему отбору, а таблица листается.
   */
  closedSummary: (params: Query) => ['history-summary', params],
  /**
   * Срез «На объекте» (ADR 0036): какая техника стоит на площадках прямо сейчас. День среза
   * считает сервер и возвращает в ответе — от него, а не от часов браузера, строятся подписи
   * присутствия.
   */
  onSite: (params: Query) => ['on-site', params],
  /** Итог среза по тем же фильтрам: сколько машин, на скольких объектах, кто вышел и кто уезжает. */
  onSiteSummary: (params: Query) => ['on-site-summary', params],
  /**
   * Удалённые заявки — вкладка «Архив», право `archive.read`.
   *
   * Имя говорит «удалённые», а не «архив», чтобы вкладку не путали с журналом закрытых: закрытая
   * заявка лежит в `closedList`, а сюда попадает только снесённая. Ходит при этом та же ручка
   * `list`, что и за рабочим списком, — разница в отборе: `archive: 'only'` и свой порядок, по
   * времени удаления. Общий с рабочим списком ключ подсунул бы архиву его строки.
   */
  deletedList: (params: Query) => ['archive', params],
  /**
   * Заявки-кандидаты в рейс: подтверждённые грузоперевозки того же дня, из которых окно рейса
   * набирает задание (ADR 0059, ADR 0064).
   *
   * В ключе одна дата, хотя ручку зовут с шестью параметрами: остальные пять у этого запроса
   * постоянные — статус, вид заявки, страница и её размер, — и в ключе они обещали бы ячейке
   * разнообразие, которого у неё нет. День рейса обязателен: он и есть весь вопрос.
   *
   * `undefined` приходит, пока рейс не открыт, — запрос тогда выключен (`enabled`), но ключ
   * собирается всё равно.
   */
  forRoute: (routeDate: string | undefined) => ['for-route', routeDate],
  /**
   * Заявка по идентификатору: карточка, открытая ссылкой из состава рейса, журнала листов или
   * адресной строки. Спрашивается по идентификатору, а не ищется в загруженном списке — та же
   * заявка может лежать на другой его странице или под другим фильтром.
   *
   * Сегмента с именем семейства нет — ключ так и остаётся `['vehicle-requests', id]`. Это не
   * забытая `detail`: сегмент сменил бы ячейку кэша четырём экранам сразу и заодно вывел бы
   * карточку из-под поддеревьев заявки, префиксом над которыми она стоит (см. шапку).
   */
  detail: (id: string) => [id],
  /**
   * События заявки в хронологии: заведение, правки, смены статусов (ADR 0015) — лента в карточке.
   *
   * Имя не `history` по той же причине, что и у журнала закрытых, и путать их особенно легко:
   * «что с заявкой делали» — это не «кто и с какой даты на ней работал». Сегмент остаётся
   * `'history'`, его собрал экран.
   */
  events: (id: string | undefined) => [id, 'history'],
  /**
   * Контакт назначенного водителя (ADR 0122): полем DTO он не является — ручка закрыта правом на
   * путевые листы, — отсюда и отдельный запрос, и отдельный ключ.
   */
  driver: (id: string | undefined) => [id, 'driver'],
  /**
   * Листы, выписанные по заявке (ADR 0041): их печатают из карточки, не уходя в журнал. Один ключ
   * на карточку и окно назначения намеренно — вопрос у них дословно один, и открытая перед этим
   * карточка отдаёт окну ответ из кэша.
   *
   * Тип допускает и `null`, и `undefined`, хотя оба значат «заявка не выбрана»: так их держат два
   * разных окна, и требовать от них общего написания значило бы заставить одно писать `?? null`
   * на ровном месте. Ячейка от этого не двоится — ключ хэшируется через JSON, и `undefined` в
   * массиве становится тем же `null`, — а запрос в обоих случаях выключен (`enabled`).
   */
  waybills: (id: string | null | undefined) => [id, 'waybills'],
  /**
   * The request's relocations — delivering the equipment to the site and taking it away (migration
   * 0082). One key for two places, the card and the relocation block inside it: their question is
   * one, and a second key would mean a second trip for an already loaded list. The dialog returning
   * the request to "new" no longer asks for them: what the return removes is computed by the server
   * in one answer (ADR 0211).
   *
   * Empty is legitimate here: the equipment may have come on a carrier, with no relocation at all.
   */
  relocations: (id: string | undefined) => [id, 'relocations'],
  /**
   * Дни линейного заказа (ADR 0100): дни срока с рейсом дня, его машиной, водителем, листом и
   * часами смены. Ключ знают запрос и обе мутации планирования — разъехавшись, они оставили бы на
   * экране таблицу, не заметившую собственной правки.
   */
  days: (id: string) => [id, 'days'],
  /**
   * Смены заказа спецтехники: дни заказа целиком, включая незаполненные. Один ключ на четыре
   * места — окно подтверждения, карточку, окно закрытия и поля правки срока задним числом:
   * таблица у них одна и та же, и тянуть её четырежды незачем. Окно подтверждения к тому же
   * кладёт в эту ячейку ответ мутации напрямую (`setQueryData`), так что промах ключом оставил бы
   * на экране уже сохранённые часы незаписанными.
   *
   * Форма выпадает из ряда соседей: имя семейства стоит **перед** идентификатором, тогда как у
   * перегонов, листов, дней и событий — после. Выглядит случайной и обходится дорого: под префикс
   * карточки `['vehicle-requests', id]` этот ключ не попадает, и гашение заявки смен не касается.
   * Оставлена как есть — сегмент это ячейка кэша.
   */
  shifts: (id: string | undefined) => ['shifts', id],
  /**
   * The actual time of a freight request (readings-shifts-merge plan §8): one key for the card and
   * the completion window, which ask the same question. Under the card's prefix on purpose, unlike
   * `shifts`: a garage action on a reading invalidates the request root, and this answer is read
   * from readings.
   */
  routeTime: (id: string | undefined) => [id, 'route-time'],
  /**
   * The card's documents table (rental closing plan, D8). Under the card's prefix: an edit of the
   * request changes its attachments, which are rows of this table too, and the request root
   * invalidation must reach it.
   */
  documents: (id: string) => [id, 'documents'],
  /**
   * The card's working Sundays (ADR 0246). Under the card's prefix: a term edit moves which Sundays
   * there are and drops marks, and the request root invalidation must reach it.
   */
  workingDays: (id: string) => [id, 'working-days'],
  // Ниже — история назначения (план `docs/assignment-periods-plan.md`, §9): кто и с какой даты
  // работал на заявке, что в этой истории чинить и чем обернётся правка срока. Имя `history`
  // закреплено за ней давно и потому занято; события самой заявки живут выше, под именем `events`.
  /** История назначения заявки: из неё портал строит «Состав по датам». */
  history: (id: string) => [id, 'assignment-changes'],
  /** Осмотр двери ремонта: что в истории чинить (подэтап 6a). */
  repairState: (id: string) => [id, 'repair-state'],
  /** Последствия правки срока: ключ несёт саму команду — другой срок это другой ответ. */
  periodPreview: (id: string, command: unknown) => [id, 'period-preview', command],
});

/**
 * Подсказка рейса заявке (`vehicleRequestsApi.routePrefill`) — под СВОИМ корнем, а не семейством
 * внутри `['vehicle-requests']`.
 *
 * Так его собрал экран, и переносить корень нельзя по тому же правилу, что и сегмент: это ячейка
 * кэша. По существу форма верна: отвечает подсказка не о заявке, а о рейсах дня — какие уже
 * заведены, каким бланком закрываются, чем были заполнены графы шапки в прошлый раз, — и
 * устаревает от правки рейсов. Под корнем заказа её сбрасывала бы каждая мутация модуля, а их
 * здесь десятки.
 *
 * Обратная сторона названа прямо: `['route-prefill']` сегодня не гасит никто и ничем — ни
 * заведение рейса, ни перенос заявки в чужой. Окно берёт подсказку при открытии и живёт с ней до
 * закрытия, и пока окно короткое, это сходит с рук. Завести гашение — правка поведения, и
 * делается она отдельно, с проверкой глазами, а не попутно переносом ключа.
 */
export const routePrefillKeys = createQueryKeys('route-prefill', {
  /**
   * The date is part of the request. When it is absent, assignment and route transfer ask the
   * same endpoint for the same request and share this cache entry. A null id represents a closed
   * window whose query remains disabled.
   */
  onTripDate: (id: string | null, date: string | undefined) => [id, date],
});
